'use client';

import { useState, useEffect } from 'react';
import { ClipData } from '@/components/ClipCard';
import { getStoredClips, onClipsUpdated } from '@/lib/clipStore';

interface UseClipsOptions {
  sort?: 'trending' | 'newest' | 'most-downloaded' | 'most-liked' | 'editors-picks';
  category?: string;
  limit?: number;
  creatorUsername?: string;
}

interface UseClipsResult {
  clips: ClipData[];
  loading: boolean;
  isEmpty: boolean;
}

/**
 * Hook to fetch clips — reads from the shared local store (uploaded by users).
 * Replace getStoredClips() with a real API/Supabase call when a backend is connected.
 */
export function useClips(options: UseClipsOptions = {}): UseClipsResult {
  const [clips, setClips] = useState<ClipData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    function loadClips() {
      let all = getStoredClips();

      // Filter by category
      if (options.category && options.category !== 'all') {
        all = all.filter((c) => Array.isArray(c.categories) ? c.categories.includes(options.category!) : (c as any).category === options.category);
      }

      // Filter by creator
      if (options.creatorUsername) {
        all = all.filter((c) => {
          // Prefer exact username match if stored
          if (c.uploaderUsername) {
            return c.uploaderUsername.toLowerCase() === options.creatorUsername!.toLowerCase();
          }
          // Fallback: convert display name to username-style
          return c.uploader.toLowerCase().replace(/\s+/g, '.') === options.creatorUsername!.toLowerCase();
        });
      }

      // Sort
      if (options.sort === 'trending') {
        all = [...all].sort((a, b) => b.downloads + b.likes - (a.downloads + a.likes));
      } else if (options.sort === 'newest') {
        // Already stored newest-first; no extra sort needed
      } else if (options.sort === 'most-downloaded') {
        all = [...all].sort((a, b) => b.downloads - a.downloads);
      } else if (options.sort === 'most-liked') {
        all = [...all].sort((a, b) => b.likes - a.likes);
      } else if (options.sort === 'editors-picks') {
        // Show first 4 newest as editor picks
        all = all.slice(0, 4);
      }

      // Limit
      if (options.limit) {
        all = all.slice(0, options.limit);
      }

      setClips(all);
      setLoading(false);
    }

    loadClips();

    // Re-load whenever a new clip is uploaded (same tab)
    const unsubscribe = onClipsUpdated(loadClips);
    return unsubscribe;
  }, [options.sort, options.category, options.limit, options.creatorUsername]);

  return { clips, loading, isEmpty: !loading && clips.length === 0 };
}

/**
 * Hook to fetch a single creator's profile data.
 */
export interface CreatorProfile {
  name: string;
  username: string;
  avatar: string;
  banner: string;
  bio: string;
  verified: boolean;
  premium: boolean;
  followers: number;
  following: number;
  clips: number;
  totalViews: number;
  totalDownloads: number;
  totalLikes: number;
}

export function useCreatorProfile(username: string) {
  const [creator, setCreator] = useState<CreatorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;

    async function fetchCreator() {
      try {
        // TODO: Replace with real API call, e.g.:
        // const res = await fetch(`/api/creators/${username}`);
        // if (res.status === 404) { if (!cancelled) setNotFound(true); return; }
        // const data = await res.json();
        // if (!cancelled) setCreator(data);

        // Until a real backend is connected, show not found
        if (!cancelled) setNotFound(true);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCreator();
    return () => { cancelled = true; };
  }, [username]);

  return { creator, loading, notFound };
}

/**
 * Hook to fetch requests from the backend.
 */
export function useRequests() {
  const [requests, setRequests] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchRequests() {
      try {
        // TODO: Replace with real API call
        if (!cancelled) setRequests([]);
      } catch {
        if (!cancelled) setRequests([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchRequests();
    return () => { cancelled = true; };
  }, []);

  return { requests, loading, isEmpty: !loading && requests.length === 0 };
}

/**
 * Hook to fetch notifications from the backend.
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<unknown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchNotifications() {
      try {
        // TODO: Replace with real API call
        if (!cancelled) setNotifications([]);
      } catch {
        if (!cancelled) setNotifications([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchNotifications();
    return () => { cancelled = true; };
  }, []);

  return { notifications, loading, isEmpty: !loading && notifications.length === 0 };
}
